import { type ApolloError } from "@apollo/client"
import { Button, Modal, ModalBody, ModalContent, ModalOverlay, useDisclosure, useToast, type ButtonProps } from "@chakra-ui/react"
import { useAtom } from "jotai"
import { useResetAtom } from "jotai/utils"
import { useCallback, type FC } from "react"
import { useNavigate } from "react-router-dom"
import { Paths } from "../../common/commonConstants"
import { userDetailsAtom } from "../../components/Menu/menu.store"
import { useCreateFmpProjectMutation } from "../../type-and-hooks"
import { Login } from "../login/login"
import NewFMPModal from "./common.fmp.actions/fmp.createfmp.modal"
import { formData } from "./common.fmp.hooks/formData.store"

interface CreateNewFMPProps {
  buttonText?: string
  buttonProps?: ButtonProps
}

export const CreateNewFMP: FC<CreateNewFMPProps> = ({ buttonProps, buttonText }): JSX.Element => {
  const [userDetails] = useAtom(userDetailsAtom)
  const [createFmpProjectMutation, { loading, client }] = useCreateFmpProjectMutation()
  const [, setFormDataAtom] = useAtom(formData)
  const toast = useToast()
  const navigate = useNavigate()

  // Modal states
  const { isOpen, onClose, onOpen } = useDisclosure()
  const { isOpen: isLoginOpen, onClose: onLoginClose, onOpen: onLoginOpen } = useDisclosure()

  const resetForm = useResetAtom(formData)

  // Navigation callback
  const navigateToEditFMP = useCallback(
    (fmpId: string): void => {
      if (location.pathname.toLowerCase() === "/fmp/product-filter") {
        return
      }
      navigate(Paths.FMP.EDIT_FMP, { state: { fmpId } })
    },
    [navigate]
  )

  // Error handling
  const handleError = useCallback(
    (error: ApolloError | Error): void => {
      let errorMessage = "An error occurred"

      // Check for the errors array structure (like in your response)
      if ("errors" in error && Array.isArray(error.errors) && error.errors.length > 0) {
        errorMessage = error.errors[0]
      } else if ("graphQLErrors" in error && error.graphQLErrors?.length > 0) {
        // Cast the GraphQL error as a string, following the pattern used throughout the codebase
        errorMessage = (error.graphQLErrors[0] as unknown as string) || error.graphQLErrors[0].message || "An error occurred"
      } else if ("message" in error) {
        errorMessage = error.message
      }

      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true
      })
    },
    [toast]
  )

  // FMP creation logic
  const handleNewFMPCreate = useCallback(
    (projectName: string): void => {
      if (!userDetails?._id || !projectName) {
        return
      }

      createFmpProjectMutation({
        variables: {
          input: {
            project_name: projectName,
            user_id: userDetails._id
          }
        }
      })
        .then(res => {
          const fmpData = res.data?.createFMPProject
          if (!fmpData) {
            throw new Error("Failed to create FMP project")
          }

          void client.resetStore()
          resetForm()

          setFormDataAtom(prevState => ({
            ...prevState,
            projectName: fmpData.project_name,
            projectAddress: fmpData.project_address,
            companyName: fmpData.subcontractor_name,
            companyAddress: fmpData.subcontractor_address,
            dateCreated: fmpData.date_created,
            dateModified: fmpData.date_modified
          }))

          if (fmpData.id) {
            navigateToEditFMP(fmpData.id)
          } else {
            toast({
              title: "Error",
              description: "Failed to fetch FMP ID",
              status: "error",
              isClosable: true
            })
          }
        })
        .catch(handleError)
    },
    [userDetails?._id, createFmpProjectMutation, client, resetForm, setFormDataAtom, navigateToEditFMP, handleError, toast]
  )

  // Button click handler
  const handleCreateNewFMP = useCallback((): void => {
    if (!userDetails?._id) {
      onLoginOpen()
      return
    }
    onOpen()
  }, [userDetails?._id, onLoginOpen, onOpen])

  // Login success handler
  const handleLoginSuccess = useCallback((): void => {
    onLoginClose()
    onOpen()
  }, [onLoginClose, onOpen])

  // Login cancel handler
  const handleLoginCancel = useCallback((): void => {
    onLoginClose()
  }, [onLoginClose])

  return (
    <>
      <Button variant="Button1Small" onClick={handleCreateNewFMP} isLoading={loading} {...buttonProps}>
        {buttonText ?? "Create New FMP"}
      </Button>

      <NewFMPModal headerTitle="Create New FMP" isOpen={isOpen} onClose={onClose} onCreateCallBack={handleNewFMPCreate} />

      {/* Login Modal */}
      <Modal isOpen={isLoginOpen} onClose={onLoginClose} size="full">
        <ModalOverlay />
        <ModalContent>
          <ModalBody padding="0">
            <Login successCallBack={handleLoginSuccess} loginCancelCallBack={handleLoginCancel} />
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  )
}
